import { ArrowDown2, ArrowUp2, Icon } from 'iconsax-react';
import React, { PropsWithChildren, ReactElement, useMemo, useRef } from 'react';

import IconButton from '@/components/IconButton';
import classNames from '@/helpers/classNames';

import InputErrorMessage from '../InputErrorMessage';
import styles from './Input.module.scss';

export type LabelWeight = 'regular' | 'medium';

interface InputProps extends React.ComponentPropsWithRef<'input'> {
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  label?: string;
  labelVariant?: 'blue' | 'darkBlue' | 'gray';
  labelWeight?: LabelWeight;
  description?: string;
  isRequiredIndicatorVisible?: boolean;
  error?: boolean | string;
  withoutArrows?: boolean;
  LeftIcon?: Icon;
  RightIcon?: Icon;
  LabelWrapper?: (props: PropsWithChildren) => ReactElement;
}

export default React.forwardRef<HTMLInputElement, InputProps>(function Input(
  props: InputProps,
  ref
) {
  const {
    label,
    labelVariant = 'blue',
    labelWeight = 'regular',
    LabelWrapper,
    description,
    className,
    error,
    LeftIcon,
    RightIcon,
    inputClassName,
    labelClassName,
    wrapperClassName,
    withoutArrows,
    isRequiredIndicatorVisible,
    ...inputProps
  } = props;

  const _ref = useRef<HTMLInputElement | null>(null);
  const isNumberInput = props.type === 'number';

  const _inputClassName = classNames([styles.input, inputClassName], {
    [styles.withLeftIcon]: LeftIcon,
    [styles.withRightIcon]: RightIcon,
    [styles.withNumberButtons]: isNumberInput,
    [styles.error]: error,
  });

  const labelComponent = useMemo(
    () =>
      label && (
        <label
          htmlFor={props.name}
          className={classNames([
            styles.label,
            styles[labelVariant],
            styles[labelWeight],
            labelClassName,
          ])}
        >
          {label}
          {isRequiredIndicatorVisible ? ' *' : ''}
        </label>
      ),
    [
      isRequiredIndicatorVisible,
      label,
      labelClassName,
      labelVariant,
      labelWeight,
      props.name,
    ]
  );

  return (
    <div
      className={classNames([styles.inputContainer, className], {
        [styles.error]: error,
        [styles.disabled]: props.disabled,
      })}
    >
      {LabelWrapper ? (
        <LabelWrapper>{labelComponent}</LabelWrapper>
      ) : (
        <>{labelComponent}</>
      )}
      <div className={classNames([styles.inputWrapper, wrapperClassName])}>
        <input
          ref={(e) => {
            if (typeof ref === 'function') {
              ref(e);
            }

            _ref.current = e;
          }}
          id={props.name}
          {...inputProps}
          className={_inputClassName}
        />
        {LeftIcon && (
          <LeftIcon
            variant="Linear"
            color="currentColor"
            size={16}
            className={classNames([styles.icon, styles.leftIcon])}
          />
        )}
        {RightIcon && (
          <RightIcon
            variant="Linear"
            color="currentColor"
            size={16}
            className={classNames([styles.icon, styles.rightIcon])}
          />
        )}
        {isNumberInput && !withoutArrows && (
          <div className={styles.numberButtons}>
            <IconButton
              Icon={ArrowUp2}
              variant="white"
              size="small"
              type="button"
              iconSize={16}
              disabled={props.disabled}
              onClick={() => {
                _ref?.current?.stepUp();
                _ref?.current?.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
              }}
              className={classNames([styles.numberButton, styles.upButton])}
            />
            <IconButton
              Icon={ArrowDown2}
              variant="white"
              size="small"
              type="button"
              iconSize={16}
              disabled={props.disabled}
              onClick={() => {
                _ref?.current?.stepDown();
                _ref?.current?.dispatchEvent(
                  new Event('input', { bubbles: true })
                );
              }}
              className={classNames([styles.numberButton, styles.downButton])}
            />
          </div>
        )}
      </div>
      {description && <p className={styles.description}>{description}</p>}
      <InputErrorMessage error={error} />
    </div>
  );
});
